const maximumUint256 = (1n << 256n) - 1n;

function decimals(value: number): void {
  if (!Number.isInteger(value) || value < 0 || value > 255) {
    throw new Error('Token decimals must be an integer from 0 to 255');
  }
}

function gcd(a: bigint, b: bigint): bigint {
  while (b !== 0n) [a, b] = [b, a % b];
  return a;
}

/**
 * Converts a displayed quote-token price per ONE base token into the engine's
 * quote-raw/base-raw threshold fraction. Never uses floating point or rounds.
 * Decimals must come from the verified authorized route, not a ticker lookup.
 * This is encoding only, not a price observation, order authorization or fill guarantee.
 */
export function encodeExternalOrderTriggerPrice(input: {
  price: string;
  baseDecimals: number;
  quoteDecimals: number;
}): { numerator: bigint; denominator: bigint } {
  decimals(input.baseDecimals);
  decimals(input.quoteDecimals);
  if (typeof input.price !== 'string' || input.price.length > 160) {
    throw new Error('Price must be a bounded decimal string');
  }
  const match = /^(0|[1-9][0-9]*)(?:\.([0-9]+))?$/.exec(input.price);
  if (!match) throw new Error('Price must be a plain positive decimal');
  const fraction = match[2] ?? '';
  const mantissa = BigInt(`${match[1]}${fraction}`);
  if (mantissa === 0n) throw new Error('Trigger price must be greater than zero');
  let numerator = mantissa * 10n ** BigInt(input.quoteDecimals);
  let denominator = 10n ** BigInt(fraction.length + input.baseDecimals);
  const divisor = gcd(numerator, denominator);
  numerator /= divisor;
  denominator /= divisor;
  if (numerator > maximumUint256 || denominator > maximumUint256) {
    throw new Error('Exact trigger price exceeds the on-chain fraction bounds');
  }
  return { numerator, denominator };
}
